import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import './KeyAccountPO.css';

/**
 * Key Account PO — Barnes & Noble purchase order in, everything out.
 *
 * 1. Upload the PO (the B&N fax PDF or Denise's PO spreadsheet).
 * 2. Every line is checked against NetSuite: item exists, barcode on the item,
 *    casepack matches our case item, inner packs (IOQ) required, retail confirmed.
 *    Blocking flags stop label generation until they are cleared in NetSuite.
 * 3. Enter carton weights, confirm retail, then download the FineLine upload
 *    sheet, the Maestro sticker CSV, and print the pack plan + sign-off checklist.
 *
 * Nothing downstream is retyped from the PO by hand. See keyAccountPORoutes.js.
 */

function money(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '';
}

function downloadBlob(data, filename, type) {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Flag({ flag }) {
  return (
    <li className={`ka-flag ka-flag-${flag.severity}`}>
      <span className="ka-flag-code">{flag.code.replace(/_/g, ' ')}</span>
      <span className="ka-flag-msg">{flag.message}</span>
    </li>
  );
}

export default function KeyAccountPO() {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [checked, setChecked] = useState(null);
  const [weights, setWeights] = useState({});
  const [retailOk, setRetailOk] = useState({});
  const [overage, setOverage] = useState('10');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const [linked, setLinked] = useState(null); // result of /attach: PO stored + linked to sales orders
  const [run, setRun] = useState(null); // saved run { id, status } from /check
  const [runStatus, setRunStatus] = useState(null); // station checks summary for the run
  const printRef = useRef(null);

  const loadRunStatus = async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`/api/key-account-po/runs/${id}`);
      setRunStatus(res.data.run);
    } catch (err) {
      setRunStatus(null);
    }
  };

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: 'Key Account PO pack plan' });

  const allLines = useMemo(() => (checked ? checked.pos.flatMap((po) => po.lines.map((l) => ({ ...l, poNumber: po.poNumber }))) : []), [checked]);
  const vendorItems = useMemo(() => [...new Set(allLines.map((l) => l.vendorItem))], [allLines]);
  const allFlags = useMemo(() => (checked ? checked.pos.flatMap((po) => [
    ...po.flags.map((f) => ({ ...f, where: `PO ${po.poNumber}` })),
    ...po.lines.flatMap((l) => l.flags.map((f) => ({ ...f, where: `PO ${po.poNumber} · ${l.vendorItem}` }))),
  ]) : []), [checked]);
  const blocks = allFlags.filter((f) => f.severity === 'block');
  const warns = allFlags.filter((f) => f.severity === 'warn');

  const weightsMissing = vendorItems.filter((v) => !weights[v] || Number(weights[v]) <= 0);
  const retailMissing = vendorItems.filter((v) => !retailOk[v]);
  const canGenerate = checked && blocks.length === 0 && weightsMissing.length === 0 && retailMissing.length === 0;

  const reset = () => {
    setParsed(null); setChecked(null); setPlan(null); setError(''); setWeights({}); setRetailOk({}); setRun(null); setRunStatus(null); setLinked(null);
  };

  const checkTotals = useMemo(() => {
    if (!runStatus) return null;
    const totals = {};
    for (const perLine of Object.values(runStatus.counts || {})) {
      for (const [type, c] of Object.entries(perLine)) {
        totals[type] = totals[type] || { pass: 0, fail: 0 };
        totals[type].pass += c.pass || 0;
        totals[type].fail += c.fail || 0;
      }
    }
    return totals;
  }, [runStatus]);

  const onFile = (e) => {
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
    reset();
  };

  const runCheck = async () => {
    if (!file) return;
    setBusy('Reading the PO');
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const p = await axios.post('/api/key-account-po/parse', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setParsed(p.data);
      setBusy('Checking every line against NetSuite');
      const c = await axios.post('/api/key-account-po/check', p.data);
      setChecked(c.data);
      setRun(c.data.run || null);
      if (c.data.run && c.data.run.id) loadRunStatus(c.data.run.id);
      const pp = await axios.post('/api/key-account-po/generate/pack-plan', { checked: c.data });
      setPlan(pp.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const genFineline = async () => {
    setBusy('Building the FineLine sheet');
    setError('');
    try {
      const res = await axios.post('/api/key-account-po/generate/fineline', {
        checked, weights, overage: Number(overage) / 100, runId: run && run.id ? run.id : null,
      }, { responseType: 'blob' });
      const stem = checked.pos.map((p) => p.poNumber).join('_');
      downloadBlob(res.data, `BARNES___NOBLE_${stem}.xlsx`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const genStickers = async () => {
    setBusy('Building the sticker file');
    setError('');
    try {
      const res = await axios.post('/api/key-account-po/generate/stickers', { checked }, { responseType: 'text' });
      const stem = checked.pos.map((p) => p.poNumber).join('_');
      downloadBlob(res.data, `maestro_stickers_${stem}.csv`, 'text/csv');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const storeAndLink = async () => {
    if (!file || !checked) return;
    setBusy('Storing the PO and linking it to the sales order');
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('pos', JSON.stringify(checked.pos.map((p) => ({ poNumber: p.poNumber, poDate: p.poDate }))));
      const res = await axios.post('/api/key-account-po/attach', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLinked(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="ka-app">
      <header className="ka-header">
        <div>
          <h1>Key Account PO</h1>
          <p className="ka-sub">Barnes &amp; Noble &middot; PO in, labels and pack plan out</p>
        </div>
        <div className="ka-note">
          Every carton label, sticker and inner pack count comes from the PO lines. Nothing is retyped.
        </div>
      </header>

      <section className="ka-card">
        <label className="ka-field-label" htmlFor="ka-file">1. Upload the PO</label>
        <div className="ka-upload-row">
          <input id="ka-file" type="file" accept=".pdf,.xlsx,.xlsm,application/pdf" onChange={onFile} />
          <button type="button" className="ka-btn ka-btn-primary" onClick={runCheck} disabled={!file || !!busy}>
            Read and check
          </button>
        </div>
        <div className="ka-hint">The B&amp;N PO fax PDF (from DC_PO_CONTACTS) or the PO spreadsheet from Inventory Control.</div>
        {busy && <div className="ka-hint">{busy}&hellip;</div>}
        {error && <div className="ka-error">{error}</div>}
        {parsed && parsed.warnings && parsed.warnings.length > 0 && (
          <ul className="ka-warnings">
            {parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        )}
      </section>

      {checked && (
        <>
          <section className="ka-card">
            <div className="ka-field-label">2. What the PO says</div>
            {checked.pos.map((po) => (
              <div key={po.poNumber} className="ka-po">
                <div className="ka-po-head">
                  <strong>PO {po.poNumber}</strong>
                  <span>{po.dc.city}, {po.dc.state} ({po.dc.code}) &middot; SAN {po.dc.san}</span>
                  <span>PO date {po.poDate} &middot; Cancel {po.cancelDate || 'not on sheet'}</span>
                  <span>{po.summary.totalUnits} units &middot; {po.summary.totalCartons} cartons &middot; {po.summary.totalInnerPacks} inner packs</span>
                </div>
                <table className="ka-table">
                  <thead>
                    <tr>
                      <th>Item</th><th>EAN</th><th>NetSuite</th><th>Units</th><th>Casepack</th><th>IOQ</th>
                      <th>Cartons</th><th>Inner packs</th><th>Cost</th><th>Retail</th><th>Arrival</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.lines.map((l, i) => {
                      const blocked = l.flags.some((f) => f.severity === 'block');
                      const warned = l.flags.some((f) => f.severity === 'warn');
                      return (
                        <tr key={i} className={blocked ? 'ka-row-block' : warned ? 'ka-row-warn' : ''}>
                          <td>{l.vendorItem}<br /><small>{l.title}</small></td>
                          <td>{l.ean13}</td>
                          <td>
                            {l.ns.caseItem ? <span>{l.ns.caseItem.itemid} (case of {l.ns.caseItem.caseQtyFromDescription || '?'})</span> : <span className="ka-bad">not in NetSuite</span>}
                            <br />
                            <small>{l.ns.eanOwner ? `EAN on ${l.ns.eanOwner.itemid}` : 'EAN on no item'}</small>
                          </td>
                          <td>{l.qty}</td>
                          <td>{l.casepack}</td>
                          <td>{l.ioq}{l.innerPacksPerCarton && l.ioq < l.casepack ? <><br /><small>{l.innerPacksPerCarton} packs of {l.ioq}</small></> : null}</td>
                          <td>{l.cartons}</td>
                          <td>{l.ioq < l.casepack ? l.innerPacks : 'none'}</td>
                          <td>{money(l.cost)}</td>
                          <td>{money(l.retail)}</td>
                          <td>{l.expectedArrival}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </section>

          <section className="ka-card">
            <div className="ka-field-label">3. Flags</div>
            {blocks.length === 0 && warns.length === 0 && <div className="ka-hint">No flags. Everything on the PO matches NetSuite.</div>}
            {blocks.length > 0 && (
              <>
                <div className="ka-flag-title ka-bad">{blocks.length} blocking &mdash; fix in NetSuite, then upload again</div>
                <ul className="ka-flags">{blocks.map((f, i) => <Flag key={`b${i}`} flag={{ ...f, message: `${f.where}: ${f.message}` }} />)}</ul>
              </>
            )}
            {warns.length > 0 && (
              <>
                <div className="ka-flag-title">{warns.length} to confirm</div>
                <ul className="ka-flags">{warns.map((f, i) => <Flag key={`w${i}`} flag={{ ...f, message: `${f.where}: ${f.message}` }} />)}</ul>
              </>
            )}
          </section>

          <section className="ka-card">
            <div className="ka-field-label">4. Carton weights and retail confirmation</div>
            <table className="ka-table ka-table-inputs">
              <thead><tr><th>Item</th><th>Casepack</th><th>Carton weight (lbs)</th><th>Retail on PO</th><th>Confirm retail</th></tr></thead>
              <tbody>
                {vendorItems.map((v) => {
                  const l = allLines.find((x) => x.vendorItem === v);
                  return (
                    <tr key={v}>
                      <td>{v}<br /><small>{l.title}</small></td>
                      <td>{l.casepack}</td>
                      <td>
                        <input
                          type="number" min="0" step="0.1" className="ka-input"
                          value={weights[v] || ''}
                          onChange={(e) => setWeights({ ...weights, [v]: e.target.value })}
                          placeholder="lbs"
                        />
                      </td>
                      <td>{money(l.retail)}</td>
                      <td>
                        <label className="ka-check">
                          <input type="checkbox" checked={!!retailOk[v]} onChange={(e) => setRetailOk({ ...retailOk, [v]: e.target.checked })} />
                          Labels print {money(l.retail)}
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="ka-overage">
              <label htmlFor="ka-overage">Label overage</label>
              <input id="ka-overage" type="number" min="0" max="50" step="1" className="ka-input ka-input-short" value={overage} onChange={(e) => setOverage(e.target.value)} />
              <span>% extra labels ordered from FineLine</span>
            </div>
          </section>

          <section className="ka-card">
            <div className="ka-field-label">5. Generate</div>
            {!canGenerate && (
              <div className="ka-hint">
                {blocks.length > 0 && <div>Blocked until the {blocks.length} blocking flag{blocks.length === 1 ? '' : 's'} above {blocks.length === 1 ? 'is' : 'are'} cleared.</div>}
                {weightsMissing.length > 0 && <div>Enter a carton weight for: {weightsMissing.join(', ')}.</div>}
                {retailMissing.length > 0 && <div>Confirm the retail price for: {retailMissing.join(', ')}.</div>}
              </div>
            )}
            <div className="ka-actions">
              <button type="button" className="ka-btn ka-btn-primary" onClick={genFineline} disabled={!canGenerate || !!busy}>Download FineLine sheet</button>
              <button type="button" className="ka-btn" onClick={genStickers} disabled={!canGenerate || !!busy}>Download sticker CSV (Maestro)</button>
              <button type="button" className="ka-btn" onClick={handlePrint} disabled={!plan || !!busy}>Print pack plan and checklist</button>
              <button type="button" className="ka-btn" onClick={storeAndLink} disabled={!checked || !file || !!busy}>Store PO and link to sales order</button>
            </div>
            {linked && (
              <div className="ka-linked">
                <div className="ka-linked-title">{linked.fileName}</div>
                {linked.salesOrders.filter((r) => r.so).map((r) => (
                  <div key={r.so.id} className="ka-linked-row">
                    <a href={r.linkUrl} target="_blank" rel="noreferrer">SO {r.so.tranid}</a>
                    <span> (PO {r.so.po}): </span>
                    {r.actions.map((a) => (
                      <span key={a.step} className={`ka-linked-step ka-linked-${a.status}`} title={a.error || a.reason || a.detail || ''}>
                        {a.step.replace('_', ' ')} {a.status}
                      </span>
                    ))}
                  </div>
                ))}
                {linked.unmatchedPos.length > 0 && (
                  <div className="ka-linked-row">No sales order yet for PO {linked.unmatchedPos.join(', ')}. Enter the order in NetSuite, then store again.</div>
                )}
              </div>
            )}
          </section>

          <section className="ka-card">
            <div className="ka-field-label">6. Floor checks</div>
            {run && run.id ? (
              <>
                <div className="ka-hint">
                  Saved as run <code>{run.id}</code>
                  {checked.salesOrders && checked.salesOrders.length > 0
                    ? <> &middot; sales order{checked.salesOrders.length === 1 ? '' : 's'} {checked.salesOrders.map((s) => `${s.tranid} (PO ${s.poNumber})`).join(', ')}</>
                    : <> &middot; no sales order in NetSuite yet for these PO numbers</>}
                  &middot; <a href="/station/key-account-check" target="_blank" rel="noreferrer">Open the Key Account Check Station</a>
                  {' '}<button type="button" className="ka-btn ka-btn-small" onClick={() => loadRunStatus(run.id)}>Refresh</button>
                </div>
                {runStatus && (
                  <div className="ka-status">
                    <div className={`ka-status-state ${runStatus.status === 'passed' ? 'ka-ok' : ''}`}>
                      {runStatus.status === 'passed' ? 'Signed off on the floor' : runStatus.signoff.ready ? 'All checks passed; waiting for sign-off' : `${runStatus.signoff.missing.length} checks still open`}
                    </div>
                    <table className="ka-table ka-table-inputs">
                      <thead><tr><th>Check</th><th>Pass</th><th>Fail</th></tr></thead>
                      <tbody>
                        {['carton_scan', 'carton_photo', 'sticker_scan', 'sticker_photo', 'inner_scan', 'inner_pack_photo'].map((t) => (
                          <tr key={t}>
                            <td>{t.replace(/_/g, ' ')}</td>
                            <td>{checkTotals && checkTotals[t] ? checkTotals[t].pass : 0}</td>
                            <td className={checkTotals && checkTotals[t] && checkTotals[t].fail ? 'ka-bad' : ''}>{checkTotals && checkTotals[t] ? checkTotals[t].fail : 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <ul className="ka-flags">
                      {runStatus.manualItems.map((m) => {
                        const s = runStatus.manual && runStatus.manual[m.key];
                        return <li key={m.key} className={`ka-flag ${s ? (s.result === 'pass' ? 'ka-flag-ok' : 'ka-flag-block') : 'ka-flag-warn'}`}><span className="ka-flag-code">{s ? (s.result === 'pass' ? 'done' : 'problem') : 'open'}</span><span className="ka-flag-msg">{m.label}{s ? ` (${s.operator || 'operator'})` : ''}</span></li>;
                      })}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="ka-hint">{run && run.note ? run.note : 'The run is saved when the check completes, so the floor station can verify against it.'}</div>
            )}
          </section>

          {plan && (
            <div className="ka-print-wrap">
              <div ref={printRef} className="ka-print">
                <h1>Key Account PO &mdash; pack plan and sign-off</h1>
                <p className="ka-meta">Barnes &amp; Noble &middot; pallet max height {plan.pos[0]?.palletMaxHeightIn || 58}&quot;</p>
                {plan.pos.map((po) => (
                  <section key={po.poNumber} className="ka-print-po">
                    <h2>PO {po.poNumber} &mdash; {po.dc.city}, {po.dc.state} ({po.dc.code})</h2>
                    <p className="ka-meta">PO date {po.poDate} &middot; Expected arrival {po.expectedArrival} &middot; Cancel {po.cancelDate || 'n/a'} &middot; Route by {po.routingDueBy} (estimate)</p>
                    <table className="ka-table">
                      <thead><tr><th>Item</th><th>EAN</th><th>Units</th><th>Casepack</th><th>IOQ</th><th>Cartons</th><th>Inner packs</th><th>Carton label</th><th>Retail</th></tr></thead>
                      <tbody>
                        {po.lines.map((l) => (
                          <tr key={l.vendorItem}>
                            <td>{l.vendorItem}<br /><small>{l.title}</small></td>
                            <td>{l.ean13}</td>
                            <td>{l.units}</td>
                            <td>{l.casepack}</td>
                            <td>{l.ioq}{l.unitsPerInnerPack ? <><br /><small>{l.innerPacksPerCarton} packs of {l.unitsPerInnerPack} per carton</small></> : null}</td>
                            <td>{l.cartons}</td>
                            <td>{l.innerPacks === null ? 'none' : l.innerPacks}</td>
                            <td>{l.cartonLabelText}{l.ioqLabelText ? <><br /><small>{l.ioqLabelText}</small></> : null}</td>
                            <td>{money(l.retail)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="ka-meta">Totals: {po.totals.units} units, {po.totals.cartons} cartons, {po.totals.innerPacks} inner packs</p>
                  </section>
                ))}
                <h2>Sign-off before pickup</h2>
                <ol className="ka-checks">
                  {[
                    'Inner packs built to the IOQ count and each inner pack carries an IOQ label with the EAN',
                    'Carton product label shows the RETAIL price from the PO, not our cost',
                    'Product sticker scanned and the barcode matches the PO EAN for that line',
                    'Product sticker shows the PO retail price',
                    'Carton quantity on the label matches the units actually in the carton',
                    'Pallets no taller than 58 inches, shrink-wrapped, 4-way wooden stringer pallet',
                    'Two pallet signs on opposite sides of every pallet',
                    'Bill of lading lists every PO number on the load',
                    'Routing request sent to Jarrett at least 3 business days before the ship date',
                    'Invoice emailed to BNMerch_InvoiceCapture@concursolutions.com with vendor number 16995',
                  ].map((c) => (
                    <li key={c}><span className="ka-box" /><span className="ka-check-text">{c}</span><span className="ka-sign">Initials ______ Date ______</span></li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
